/**
 * 외부 서비스 연결 API client — 서비스 관리 화면.
 * admin BFF 자체 저장소(/api/admin/services/external/*) 호출. chat-server 무수정.
 */
import { apiClient } from './client';

export type TokenScheme = 'bearer' | 'raw' | 'none';

export interface ExternalServiceView {
  id: string;
  name: string;
  base_url: string;
  token_header: string;
  token_scheme: TokenScheme;
  has_token: boolean;
  token_preview: string | null;
  health_path: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceTestResult {
  ok: boolean;
  status?: number;
  latency_ms: number;
  detail: string;
}

export interface ServiceInput {
  name: string;
  base_url: string;
  token_header?: string;
  token_scheme?: TokenScheme;
  token?: string;
  health_path?: string;
  enabled?: boolean;
}

export async function listExternalServices(): Promise<ExternalServiceView[]> {
  const { data } = await apiClient.get<{ services: ExternalServiceView[] }>('/admin/services/external');
  return Array.isArray(data?.services) ? data.services : [];
}

export async function createExternalService(input: ServiceInput): Promise<ExternalServiceView> {
  const { data } = await apiClient.post<ExternalServiceView>('/admin/services/external', input);
  return data;
}

export async function updateExternalService(id: string, patch: Partial<ServiceInput>): Promise<ExternalServiceView> {
  const { data } = await apiClient.patch<ExternalServiceView>(`/admin/services/external/${id}`, patch);
  return data;
}

export async function deleteExternalService(id: string): Promise<void> {
  await apiClient.delete(`/admin/services/external/${id}`);
}

export async function testExternalService(id: string): Promise<ServiceTestResult> {
  const { data } = await apiClient.post<ServiceTestResult>(`/admin/services/external/${id}/test`, {}, { timeout: 12_000 });
  return data;
}

export async function testExternalServiceDraft(input: ServiceInput): Promise<ServiceTestResult> {
  const { data } = await apiClient.post<ServiceTestResult>('/admin/services/external/test', input, { timeout: 12_000 });
  return data;
}

// ─── 사용자 정의 서비스(custom) — admin 자체 레지스트리 ───────────────────────
export interface CustomService {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  configuration: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}
export interface CustomServiceInput {
  name: string;
  description?: string;
  enabled?: boolean;
  configuration?: Record<string, unknown>;
}

export async function listCustomServices(): Promise<CustomService[]> {
  const { data } = await apiClient.get<{ services: CustomService[] }>('/admin/services/custom');
  return Array.isArray(data?.services) ? data.services : [];
}
export async function createCustomService(input: CustomServiceInput): Promise<CustomService> {
  const { data } = await apiClient.post<CustomService>('/admin/services/custom', input);
  return data;
}
export async function updateCustomService(id: string, patch: Partial<CustomServiceInput>): Promise<CustomService> {
  const { data } = await apiClient.patch<CustomService>(`/admin/services/custom/${id}`, patch);
  return data;
}
export async function deleteCustomService(id: string): Promise<void> {
  await apiClient.delete(`/admin/services/custom/${id}`);
}
